// Fejlsporing og driftsstatus (Freemi-konsollen › Fejl og Status). Fejl fra browseren og edge-funktionerne renses for
// persondata (e-mail, telefon, CPR, tokens, id'er), før de sendes – og igen i databasen (fejl_gem, 20261029830000_drift.sql).
// Uden afhængigheder, så appen, edge-funktionerne (Deno) og testene bruger samme regler.

export type FejlKilde = 'browser' | 'edge';
/** Én fejl, som den sendes til edge-funktionen "fejl" (browser) eller gemmes direkte (edge). Alt er renset. */
export type FejlRapport = { kilde: FejlKilde; besked: string; stak?: string; side?: string; version?: string; browser?: string; funktion?: string; status?: number };
/** Fejl af samme type samlet (konsollens side "Fejl"). foerste = første gang typen er set; ny = inden for det sidste døgn. */
export type FejlGruppe = {
  noegle: string; kilde: FejlKilde; besked: string; stak: string | null; funktion: string | null; antal: number;
  foerste: string; sidste: string; versioner: string[]; sider: string[]; browsere: string[]; ny: boolean;
};
export type CronKoersel = { navn: string; plan: string; status: string | null; start: string | null; ms: number | null; besked: string | null; fejl_doegn: number };
/** Konsollens side "Status": seneste cron-kørsler, køer, fejlrate og afviste kald (rate limit). */
export type DriftStatus = {
  nu: string; cron: CronKoersel[] | null;
  koe: { beskeder: number; sender: number; fejlet_doegn: number; aeldste_min: number | null; automation_forsinket: number; haendelser_venter: number };
  fejl: { time: number; doegn: number; nye_doegn: number; pr_time: number[] };
  afvist: { navn: string; antal: number }[];
  eksempel?: boolean;
};

/** Grænser: højst så mange rapporter pr. sidevisning, pr. afsender (hash af IP) og i alt. Samme tal i fejl_gem. */
export const FEJL_GRAENSE = { side: 10, afsender: { max: 30, sek: 600 }, alle: { max: 2000, sek: 3600 } };
const LAENGDE = { besked: 500, stak: 2000, side: 200, version: 40, browser: 60, funktion: 40 };

// Rækkefølgen betyder noget: tokens før e-mail (et token kan indeholde @), id'er før telefon/CPR.
const REGLER: [RegExp, string][] = [
  [/eyJ[\w-]+\.[\w-]+(?:\.[\w-]+)?/g, '[token]'],
  [/(bearer)\s+[\w.~+/=-]+/gi, '$1 [token]'],
  [/(token|token_hash|access_token|refresh_token|code|kode|key|apikey|noegle|password|adgangskode|overtag)=[^&\s"'#]+/gi, '$1=[skjult]'],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[e-mail]'],
  [/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[id]'],
  [/(?<!\d)\d{6}-?\d{4}(?!\d)/g, '[cpr]'],
  [/(?<![\d:.])(?:\+45[\s-]?)?\d{2}[\s-]?\d{2}[\s-]?\d{2}[\s-]?\d{2}(?![\d])/g, '[telefon]'],
  [/[A-Za-z0-9_-]{32,}/g, '[token]'],
];

/** Fjern persondata og hemmeligheder fra en tekst og skær den til. */
export function rensTekst(s: unknown, max = LAENGDE.besked): string {
  let t = typeof s === 'string' ? s : s == null ? '' : String(s);
  t = t.slice(0, max * 4);
  for (const [re, ny] of REGLER) t = t.replace(re, ny);
  return t.slice(0, max).trim();
}

/** Siden uden forespørgsel og id'er: '#/admin/kunder/8f2…?x=1' → '/admin/kunder/:id'. */
export function rensSide(hash: string): string {
  const sti = (hash.replace(/^#/, '').split('?')[0] || '/').split('/').map((d) => (/\d/.test(d) && d.length >= 3) || d.length > 24 ? ':id' : d).join('/');
  return rensTekst(sti, LAENGDE.side) || '/';
}

/** Kort browsernavn uden versionsdetaljer, fx "Chrome 141 · Android" (ingen fuld user agent). */
export function browserNavn(ua: string): string {
  const m = (re: RegExp) => re.exec(ua)?.[1];
  const navn = m(/Edg\/(\d+)/) ? `Edge ${m(/Edg\/(\d+)/)}` : m(/SamsungBrowser\/(\d+)/) ? `Samsung ${m(/SamsungBrowser\/(\d+)/)}`
    : m(/Firefox\/(\d+)/) ? `Firefox ${m(/Firefox\/(\d+)/)}` : m(/(?:Chrome|CriOS)\/(\d+)/) ? `Chrome ${m(/(?:Chrome|CriOS)\/(\d+)/)}`
    : m(/Version\/(\d+)[\d.]* (?:Mobile\/\w+ )?Safari/) ? `Safari ${m(/Version\/(\d+)/)}` : 'Anden';
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${navn} · ${os}` : navn;
}

/** Støj, der ikke er Freemis fejl (browserudvidelser, scripts fra andre domæner, ResizeObserver). */
export function erStoej(besked: string, stak = ''): boolean {
  return /^Script error\.?$/i.test(besked) || /ResizeObserver loop/i.test(besked) || /(chrome|moz|safari(-web)?)-extension:\/\//.test(stak + besked);
}

/** Gør en rapport klar til afsendelse: alt renset og skåret til. null = intet at sende. */
export function rensRapport(r: Omit<Partial<FejlRapport>, 'besked' | 'stak'> & { besked?: unknown; stak?: unknown }): FejlRapport | null {
  const besked = rensTekst(r.besked, LAENGDE.besked);
  if (!besked) return null;
  const stak = rensTekst(r.stak, LAENGDE.stak);
  return {
    kilde: r.kilde === 'edge' ? 'edge' : 'browser', besked, ...(stak ? { stak } : {}),
    ...(r.side ? { side: rensSide(String(r.side)) } : {}),
    ...(r.version ? { version: rensTekst(r.version, LAENGDE.version) } : {}),
    ...(r.browser ? { browser: rensTekst(r.browser, LAENGDE.browser) } : {}),
    ...(r.funktion ? { funktion: rensTekst(r.funktion, LAENGDE.funktion) } : {}),
    ...(Number.isInteger(r.status) ? { status: r.status } : {}),
  };
}

/** Samme fejltype: kilde, funktion og beskeden med tal erstattet (så "linje 12" og "linje 13" er samme type). */
export function fejlType(r: Pick<FejlRapport, 'kilde' | 'besked' | 'funktion'>): string {
  return `${r.kilde}|${r.funktion ?? ''}|${r.besked.toLowerCase().replace(/\d+/g, '#').slice(0, 200)}`;
}

export type FejlRaekke = FejlRapport & { tid: string };
const top = (xs: (string | undefined)[], n: number) => {
  const t = new Map<string, number>();
  for (const x of xs) if (x) t.set(x, (t.get(x) ?? 0) + 1);
  return [...t].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, n).map(([x]) => x);
};
/** Fejl samlet pr. type (testversionen gør det her; i drift gør platform_fejl det samme i databasen). */
export function grupperFejl(raekker: FejlRaekke[], foerste: Record<string, string>, nu = Date.now()): FejlGruppe[] {
  const g = new Map<string, FejlRaekke[]>();
  for (const r of raekker) { const k = fejlType(r); g.set(k, [...(g.get(k) ?? []), r]); }
  return [...g].map(([noegle, rs]) => {
    const s = [...rs].sort((a, b) => b.tid.localeCompare(a.tid));
    const f = foerste[noegle] ?? s[s.length - 1].tid;
    return {
      noegle, kilde: s[0].kilde, besked: s[0].besked, stak: s[0].stak ?? null, funktion: s[0].funktion ?? null, antal: s.length,
      foerste: f, sidste: s[0].tid, versioner: top(s.map((x) => x.version), 3), sider: top(s.map((x) => x.side), 5), browsere: top(s.map((x) => x.browser), 5),
      ny: nu - Date.parse(f) < 864e5,
    };
  }).sort((a, b) => b.sidste.localeCompare(a.sidste));
}

/** Daglig mail til Freemi (platform-ejeren) om nye fejltyper. Kun rensede beskeder – ingen persondata. */
export function fejlMail(typer: { besked: string; kilde: FejlKilde; funktion: string | null; antal: number; side: string | null; version: string | null }[], konsol: string): { emne: string; tekst: string; knap: string } {
  const n = typer.length;
  const linjer = typer.slice(0, 10).map((t) => `• ${t.kilde === 'edge' ? `Server (${t.funktion ?? 'ukendt'})` : `Browser${t.side ? ` (${t.side})` : ''}`}: ${t.besked} – ${t.antal} gang${t.antal === 1 ? '' : 'e'}${t.version ? `, version ${t.version}` : ''}`);
  return {
    emne: n === 1 ? 'Freemi: 1 ny fejltype' : `Freemi: ${n} nye fejltyper`,
    knap: 'Se fejl i konsollen',
    tekst: `Hej\n\nDer er set ${n === 1 ? 'en ny fejltype' : `${n} nye fejltyper`} i Freemi det seneste døgn:\n\n${linjer.join('\n')}${n > 10 ? `\n… og ${n - 10} flere` : ''}\n\nSe dem samlet under Fejl i konsollen:\n${konsol}\n\nVenlig hilsen\nFreemi`,
  };
}

/** Valgfri Sentry (SENTRY_DSN / VITE_SENTRY_DSN): adresse og indhold til Sentrys envelope-API for én renset fejl. null = ugyldig DSN. */
export function sentryKald(dsn: string, r: FejlRapport, miljoe: string): { url: string; body: string } | null {
  const m = /^https:\/\/([0-9a-f]+)@([\w.-]+)\/(\d+)$/.exec(dsn.trim());
  if (!m) return null;
  const id = Array.from({ length: 32 }, () => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join('');
  const tags = Object.fromEntries(Object.entries({ kilde: r.kilde, funktion: r.funktion, side: r.side, browser: r.browser }).filter(([, v]) => v));
  const event = { event_id: id, timestamp: Date.now() / 1000, platform: 'javascript', level: 'error', environment: miljoe, release: r.version ? `freemi@${r.version}` : undefined,
    tags, exception: { values: [{ type: r.kilde === 'edge' ? 'EdgeFejl' : 'Fejl', value: r.besked }] }, extra: r.stak ? { stak: r.stak } : undefined };
  return { url: `https://${m[2]}/api/${m[3]}/envelope/?sentry_key=${m[1]}&sentry_version=7`, body: `${JSON.stringify({ event_id: id, sent_at: new Date().toISOString() })}\n${JSON.stringify({ type: 'event' })}\n${JSON.stringify(event)}` };
}
